/* eslint-disable sort-keys -- keep route keys in a readable order (path-first) */

import { Navigate, Outlet } from 'react-router-dom';

import { NavigateWithState } from '../../../../components/NavigateWithState/NavigateWithState';
import AuthorizedGuard from '../../../../guards/AuthorizedGuard';
import CmsGuard from '../../../../guards/CmsGuard';
import RequireAuth from '../../../../guards/RequireAuth';
import RequireContext from '../../../../guards/RequireContext';
import StateGuard from '../../../../guards/StateGuard';
import { UrlParamsProcessors } from '../../../../guards/UrlParams/UrlParamsProcessors';
import AccountDetails from '../../../../pages/_legacy/Account/Account';
import ActivationPage from '../../../../pages/_legacy/Activation';
import { ApplicationDetails } from '../../../../pages/_legacy/Applications/ApplicationDetails';
import ApplicationUpdatePage from '../../../../pages/_legacy/ApplicationUpdate';
import DashboardView from '../../../../pages/_legacy/Dashboard';
import LoanDocumentsList from '../../../../pages/_legacy/LoanDocuments/LoanDocumentsList';
import LoanDocumentsSubmitted from '../../../../pages/_legacy/LoanDocuments/LoanDocumentsSubmitted';
import LoanDocumentsUpload from '../../../../pages/_legacy/LoanDocuments/LoanDocumentsUpload';
import { MainLayout } from '../../../../pages/_legacy/MainLayout/MainLayout';
import RateUpdatePage from '../../../../pages/_legacy/RateUpdate';
import SignInView from '../../../../pages/_legacy/SignIn';
import SignInVerifyPinView from '../../../../pages/_legacy/SignInVerifyPin';
import SignOutView from '../../../../pages/_legacy/SignOut';
import { VehicleRoot } from '../../../../pages/_legacy/Vehicle/VehicleRoot';
import { VERIFICATION_GATE } from '../../../../workflow/assembly/DefaultVerification/gate';
import { APPLICANT_GATE } from '../../../../workflow/assembly/UnifiedDefaultApplicant/gate';
import { CO_APPLICANT_GATE } from '../../../../workflow/assembly/UnifiedDefaultCoApplicant/gate';
import { NO_OFFERS_GATE } from '../../../../workflow/assembly/UnifiedDefaultNoOffers/gate';
import { OFFERS_GATE } from '../../../../workflow/assembly/UnifiedDefaultOffers/gate';
import { REVIEW_GATE } from '../../../../workflow/assembly/UnifiedDefaultReview/gate';
import { SIGN_UP_GATE } from '../../../../workflow/assembly/UnifiedDefaultSignUp/gate';
import { GateRoot } from '../../../../workflow/common/Gate/Gate';
import { WorkflowHub } from '../../../../workflow/common/WorkflowHub';

export const unifiedRoutes = [
  {
    path: 'sign-up/*',
    element: (
      <AuthorizedGuard>
        <WorkflowHub />
      </AuthorizedGuard>
    ),
    children: [
      {
        path: '*',
        index: true,
        element: <GateRoot gate={SIGN_UP_GATE} />,
      },
    ],
  },
  {
    path: 'sign-in',
    element: (
      <AuthorizedGuard>
        <SignInView />
      </AuthorizedGuard>
    ),
  },
  {
    path: 'sign-in/activation',
    element: (
      <CmsGuard>
        <AuthorizedGuard>
          <ActivationPage />
        </AuthorizedGuard>
      </CmsGuard>
    ),
  },

  {
    path: 'sign-in/rate-update',
    element: (
      <CmsGuard>
        <AuthorizedGuard>
          <RateUpdatePage />
        </AuthorizedGuard>
      </CmsGuard>
    ),
  },
  {
    path: 'sign-in/application-update',
    element: (
      <CmsGuard>
        <AuthorizedGuard>
          <ApplicationUpdatePage />
        </AuthorizedGuard>
      </CmsGuard>
    ),
  },
  {
    path: 'sign-in/verify/pin',
    element: (
      <AuthorizedGuard>
        <StateGuard>
          <SignInVerifyPinView />
        </StateGuard>
      </AuthorizedGuard>
    ),
  },
  {
    path: 'sign-out',
    element: <SignOutView />,
  },
  {
    path: 'loan-documents',
    element: (
      <RequireAuth>
        <UrlParamsProcessors>
          <RequireContext>
            <Outlet />
          </RequireContext>
        </UrlParamsProcessors>
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="list" />,
      },
      {
        path: 'list',
        element: <LoanDocumentsList />,
      },
      {
        path: 'retail-contract',
        element: <LoanDocumentsUpload />,
      },
      {
        path: 'service-contract',
        element: <LoanDocumentsUpload />,
      },
      {
        path: 'gap-contract',
        element: <LoanDocumentsUpload />,
      },
      {
        path: 'submitted',
        element: <LoanDocumentsSubmitted />,
      },
    ],
  },
  {
    path: 'workflow',
    element: (
      <RequireAuth>
        <WorkflowHub />
      </RequireAuth>
    ),
    children: [
      {
        path: 'applicant/*',
        element: (
          <RequireContext>
            <GateRoot gate={APPLICANT_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'co-applicant/*',
        element: (
          <RequireContext>
            <GateRoot gate={CO_APPLICANT_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'offers/*',
        element: (
          <RequireContext>
            <GateRoot gate={OFFERS_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'review/*',
        element: (
          <RequireContext>
            <GateRoot gate={REVIEW_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'verification/*',
        element: (
          <RequireContext>
            <GateRoot gate={VERIFICATION_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'no-offers/*',
        element: (
          <RequireContext>
            <GateRoot gate={NO_OFFERS_GATE} />
          </RequireContext>
        ),
      },
    ],
  },
  {
    path: '',
    element: (
      <RequireAuth>
        <UrlParamsProcessors>
          <MainLayout />
        </UrlParamsProcessors>
      </RequireAuth>
    ),
    children: [
      {
        path: '',
        index: true,
        element: <Navigate to="dashboard" replace />,
      },
      {
        path: 'dashboard/*',
        children: [
          {
            index: true,
            element: <DashboardView />,
          },
          {
            path: 'vehicle/*',
            element: (
              <RequireContext>
                <StateGuard>
                  <VehicleRoot />
                </StateGuard>
              </RequireContext>
            ),
            children: [
              {
                path: '*',
                index: true,
                element: <NavigateWithState to="application" replace />,
              },
              {
                path: 'application/*',
                element: <ApplicationDetails />,
              },
            ],
          },
          {
            path: '*',
            element: <Navigate to="" replace />,
          },
        ],
      },
      {
        path: 'details/*',
        element: (
          <RequireContext>
            <AccountDetails />
          </RequireContext>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="" replace />,
  },
];
